'use client';

/**
 * Add Credits — the one-time top-up dialog (SECONDARY path).
 *
 * Subscription is the default monetization flow: balance surfaces open the
 * Plans dialog, and THIS dialog opens from its always-visible footer button
 * (it used to be a subscribers-only footnote that went straight to
 * checkout). Here the user picks an amount and goes to Stripe checkout
 * (`mode: 'payment'` against the credit price, quantity = credits).
 *
 * Leaf dialog by design: no link back to Plans (SubscribeDialog renders
 * US — importing it here would be a module cycle).
 *
 * Chrome mirrors SubscribeDialog: portal to document.body (canvas
 * transforms would otherwise clip `fixed`), z-[210] above the wallet
 * dialog, capture-phase Esc so the parent dialog doesn't also close.
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, ArrowRight, X } from 'lucide-react';
import { useTranslation } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const PRESETS = [10, 25, 50, 100];
const MIN_CREDITS = 5;
const MAX_CREDITS = 999; // checkoutSchema cap

interface AddCreditsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddCreditsDialog({ open, onClose }: AddCreditsDialogProps) {
  const { t } = useTranslation();
  const { trackCheckoutStart } = useAnalytics();
  const [creditPriceId, setCreditPriceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(25);
  const [custom, setCustom] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetch('/api/stripe/plans')
      .then((r) => r.json())
      .then((d) => setCreditPriceId(d.creditPriceId ?? null))
      .catch(() => setCreditPriceId(null))
      .finally(() => setLoading(false));
  }, [open]);

  // Capture-phase Esc — beat FullscreenNavProvider's bubble listener AND
  // the parent Plans dialog's own capture listener (which skips Esc while
  // we're open), so one keypress closes only this dialog.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const effectiveAmount = (() => {
    if (custom.trim()) {
      const n = Number(custom);
      if (Number.isInteger(n)) return n;
      return NaN;
    }
    return amount;
  })();
  const amountValid =
    Number.isInteger(effectiveAmount) &&
    effectiveAmount >= MIN_CREDITS &&
    effectiveAmount <= MAX_CREDITS;

  const checkout = useCallback(async () => {
    if (!creditPriceId || !amountValid) return;
    setCheckoutLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: creditPriceId,
          mode: 'payment',
          quantity: effectiveAmount,
        }),
      });
      const data = await res.json();
      if (data.url) {
        trackCheckoutStart(creditPriceId);
        window.location.href = data.url;
        return;
      }
      // Prefer the API's human sentence; `error` is a machine slug
      // (`purchases_disabled`, `purchases_unavailable_region`) that must never
      // reach a person - which is exactly what a market with purchases still
      // closed would have seen.
      setError(data.message ?? data.error ?? t('subscribe.checkoutFailed'));
    } catch {
      setError(t('subscribe.somethingWentWrong'));
    } finally {
      setCheckoutLoading(false);
    }
  }, [creditPriceId, amountValid, effectiveAmount, t, trackCheckoutStart]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-credits-title"
        className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="glass-panel relative flex w-[min(440px,94vw)] flex-col overflow-hidden rounded-2xl shadow-2xl">
          <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
            <h2
              id="add-credits-title"
              className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight text-foreground"
            >
              {t('wallet.addCredits')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('workspace.canvas.node.close')}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-4 py-4">
            {error && (
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <p className="mb-3 text-xs text-muted-foreground">
              {t('wallet.addCreditsDesc')}
            </p>

            {/* Amount presets + custom */}
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((p) => {
                const selected = !custom.trim() && amount === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      setAmount(p);
                      setCustom('');
                    }}
                    className={cn(
                      'rounded-lg border px-2 py-2.5 text-sm font-semibold tabular-nums transition-colors',
                      selected
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
                    )}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min={MIN_CREDITS}
                max={MAX_CREDITS}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder={t('wallet.customAmount')}
                className={cn(
                  'h-9 w-full rounded-lg border bg-transparent px-3 text-sm outline-none transition-colors placeholder:text-muted-foreground',
                  custom.trim()
                    ? amountValid
                      ? 'border-primary'
                      : 'border-destructive/60'
                    : 'border-border focus:border-primary/60',
                )}
              />
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {t('wallet.creditsUnitHint')}
            </p>

            <button
              type="button"
              onClick={checkout}
              disabled={!amountValid || !creditPriceId || checkoutLoading || loading}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {checkoutLoading || loading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  {t('wallet.addCreditsCta', { amount: String(effectiveAmount || 0) })}
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>

          </div>
        </div>
    </div>,
    document.body,
  );
}
